import { useForm } from "react-hook-form";
import {
  Modal,
  Box,
  Typography,
  Button,
  Backdrop,
  Stack,
  TextField,
  Grid,
} from "@mui/material";
import moment from "moment";
import { UserInputAcknowledgementType } from "../../../../../domain/models/user-input-acknowledgement/user-input-acknowledgement-type";
import { UserInputAcknowledgementStore } from "../../../../../store/main/UserInputAcknowledgementStore";
import Swal from "sweetalert2";
import { useState } from "react";

interface FormValues {
  quantity_received: string;
  quantity_not_received: string;
  quantity_damaged: string;
  quantity_expired: string;
  comment?: string | null;
  receipt_date: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  data: UserInputAcknowledgementType;
  onSuccess?: () => void;
}

const AddAcknowledgementModal: React.FC<Props> = ({ open, onClose, data , onSuccess }) => {
  const { AddInputAcknowledgementState, AddInputAcknowledgement } = UserInputAcknowledgementStore();

   const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const {
    register,
    getValues,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<FormValues>({ mode: "onChange" });

  const quantityDispatched = data.quantity_dispatched;

  const quantityReceived = Number(watch("quantity_received") || 0);
  const quantityNotReceived = Number(watch("quantity_not_received") || 0);
  const quantityDamaged = Number(watch("quantity_damaged") || 0);
  const quantityExpired = Number(watch("quantity_expired") || 0);

  const totalEntered =
    quantityReceived + quantityNotReceived + quantityDamaged + quantityExpired;
  const remainingQty = quantityDispatched - totalEntered;

  const isSubmitEnabled = totalEntered === quantityDispatched;
  const isFieldDisabled = (fieldValue: number): boolean =>
    remainingQty <= 0 && fieldValue === 0;

  const quantityFields = [
    "quantity_received",
    "quantity_not_received",
    "quantity_damaged",
    "quantity_expired",
  ] as const;

  const labelMap: Record<(typeof quantityFields)[number], string> = {
    quantity_received: "Quantity Received",
    quantity_not_received: "Quantity Not Received",
    quantity_damaged: "Quantity Damaged",
    quantity_expired: "Quantity Expired",
  };

const onSubmit = async (formData: FormValues) => {

   if (isSubmitting) return;

  setIsSubmitting(true);
  const quantityTypeMap: Record<(typeof quantityFields)[number], string> = {
    quantity_received: "received",
    quantity_not_received: "not_received",
    quantity_damaged: "damaged",
    quantity_expired: "expired",
  };

  const quantities = Object.entries(formData)
    .filter(
      ([key, value]) =>
        quantityFields.includes(key as (typeof quantityFields)[number]) &&
        value !== "" &&
        value !== null &&
        Number(value) > 0
    )
    .map(([key, value]) => ({
      type: quantityTypeMap[key as keyof typeof quantityTypeMap],
      quantity: Number(value),
    }));

  const payload = {
    pos_id: data.id,
    input_id: data.input_id,
    receipt_date: formData.receipt_date,
    ...(formData.comment?.trim() && { comments: formData.comment.trim() }),
    quantities: quantities,
  };

  try {
    const response = await AddInputAcknowledgement(payload);

    if (response.success) {
      Swal.fire({
        icon: "success",
        title: "Success",
        text: response.message || "Input Acknowledged Successfully",
        timer: 2000,
        showConfirmButton: false,
      });

      onClose();
      onSuccess?.();
    } else {
      Swal.fire({
        icon: "error",
        title: "Acknowledgement Failed",
        text: response.errors?.message || "Something went wrong",
      });
    }
  } catch (err) {
    console.error("Unexpected error while submitting", err);
    Swal.fire({
      icon: "error",
      title: "Error",
      text: "Something went wrong. Please try again.",
    });
  }
  finally {
    setIsSubmitting(false);
  }
};


  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
            sx: { backgroundColor: "transparent" },
          },
        }}
      >
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "rgba(255, 255, 255, 0.95)",
            boxShadow: 2,
            p: 2,
            width: 350,
            maxWidth: "80vw",
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" mb={1}>
            Add Acknowledgement
          </Typography>

          <Grid container spacing={2}>
            {quantityFields.map((fieldKey) => {
              const fieldValue = Number(watch(fieldKey) || 0);
              const disabled = isFieldDisabled(fieldValue);

              return (
                <Grid item xs={6} key={fieldKey}>
                  <TextField
                    label={labelMap[fieldKey]}
                    fullWidth
                    margin="dense"
                    disabled={disabled}
                    {...register(fieldKey, {
                      validate: (value: string) => {
                        const total = quantityFields.reduce(
                          (sum, key) =>
                            sum +
                            (key === fieldKey
                              ? Number(value)
                              : Number(getValues(key)) || 0),
                          0
                        );

                        if (disabled) return true;
                        if (!value) return "This field is required";
                        if (!/^\d+$/.test(value))
                          return "Only numeric values are allowed";
                        if (total > quantityDispatched)
                          return `Total exceeds dispatched quantity (${quantityDispatched})`;

                        return true;
                      },
                    })}
                    error={!!errors[fieldKey] && !disabled}
                    helperText={!disabled ? errors[fieldKey]?.message : ""}
                  />
                </Grid>
              );
            })}
          </Grid>

          <TextField
            label="Comment"
            fullWidth
            margin="dense"
            multiline
            rows={1}
            inputProps={{ maxLength: 50 }}
            {...register("comment", {
              maxLength: {
                value: 50,
                message: "Maximum 50 characters allowed",
              },
            })}
            error={!!errors.comment}
            helperText={
              errors.comment?.message || `${watch("comment")?.length || 0}/50`
            }
          />

          <TextField
            label="Receipt Date"
            type="date"
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            {...register("receipt_date", {
              required: "Date is required",
              validate: (value) => {
                const selectedDate = moment(value);
                const start = moment().subtract(1, "months").startOf("month");
                const today = moment().endOf("day");

                if (!selectedDate.isBetween(start, today, "day", "[]")) {
                  return `Date must be between ${start.format(
                    "MMM D, YYYY"
                  )} and ${today.format("MMM D, YYYY")}`;
                }

                return true;
              },
            })}
            error={!!errors.receipt_date}
            helperText={errors.receipt_date?.message}
          />

          <Typography
            mt={1}
            variant="body2"
            color={remainingQty < 0 ? "error" : "textSecondary"}
          >
            Total Acknowledged: {totalEntered} / {quantityDispatched}
          </Typography>

          <Stack direction="row" spacing={2} justifyContent="flex-end" mt={1}>
            <Button variant="outlined" onClick={onClose}>
              Close
            </Button>
            <Button
              variant="contained"
              type="submit"
               disabled={!isSubmitEnabled || isSubmitting}
            >
             {isSubmitting ? "Submitting..." : "Submit"}
            </Button>
          </Stack>
        </Box>
      </Modal>
    </>
  );
};

export default AddAcknowledgementModal;
